import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { forgotPassword } from '../services/auth';
import { getErrorMessage } from '../services/api';

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await forgotPassword(email);

      if (response.success) {
        setSuccess(true);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
            <h1 className="mt-6 text-2xl font-bold text-gray-900">
              {t('forgotPassword.sentTitle')}
            </h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="text-center">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {t('forgotPassword.sentMessage')}
              </div>

              <p className="text-gray-600 mb-6">
                {t('forgotPassword.checkInbox')}
              </p>

              <p className="text-sm text-gray-500 mb-6">
                {t('forgotPassword.linkValidity')}
              </p>

              <Link to="/login">
                <Button type="button" variant="primary" className="w-full">
                  {t('backToLogin', { ns: 'common' })}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-primary-600">
            Splinity
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            {t('forgotPassword.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('forgotPassword.description')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label={t('login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              autoComplete="email"
            />

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              {t('forgotPassword.submit')}
            </Button>
          </form>
        </div>

        <p className="mt-8 text-center text-sm text-gray-500">
          <Link to="/login" className="hover:text-primary-600">
            &larr; {t('backToLogin', { ns: 'common' })}
          </Link>
        </p>
      </div>
    </div>
  );
}
